/**
 * The heat demand controller's zones, as a surface can draw them.
 *
 * The hub's class (`heat_demand_controller`) aggregates thermostat outputs into
 * one boiler contact, but it names its inputs generically — `demand1`…`demand16`
 * are "Zone 1 Demand" and nothing more. The zone's *real* name lives in the
 * `demandN_source` `value_picker` setting: `{ valueId }` pointing at another
 * device's value row. Resolving that pick is what lets a sheet read "Dnevna
 * soba 45 %" instead of "Zone 2 45 %".
 *
 * The thresholds mirror `HeatDemandControllerHandler` exactly, including its
 * fallbacks and the clamp that keeps a hysteresis as large as the threshold
 * from asking for a negative release level — a surface that showed a different
 * band than the handler acts on would be worse than no surface at all.
 *
 * Framework-free and pure (no Vue, no store) so the node test runner can drive
 * it the way `shadeDevices.test.ts` drives the cover matcher.
 */

/** How the hub class declares the controller's inputs. */
const ZONE_LIMIT = 16;
const DEFAULT_ZONE_COUNT = 4;

/**
 * Value names that say no more than the device's own name — a zone's subtitle
 * is only worth showing when it does.
 */
const GENERIC_VALUE_NAMES = new Set([
  'output',
  'out',
  'value',
  'state',
  'demand',
  'heat demand',
  'heating output',
  'heating demand',
  'zone demand',
]);

/** The controller's settings after the handler's own reads and fallbacks. */
export interface HeatDemandThresholds {
  /** A zone above this raises the contact. */
  threshold: number;
  hysteresis: number;
  /** `threshold - hysteresis`, floored at 0: below this a zone stops holding. */
  releaseAt: number;
  minOnSeconds: number;
  minOffSeconds: number;
  /** 0 disables the no-source watchdog. */
  noSourceTimeoutSeconds: number;
}

/** Where a zone's demand comes from — resolved from its `value_picker` pick. */
export interface HeatDemandSource {
  /** The source device's display name (`shortName` when the hub has one). */
  deviceName: string;
  /** The source value's own name — shown only when it adds something. */
  valueName?: string | null;
}

/** One zone, ready to draw. */
export interface HeatDemandZone {
  /** 1-based, as the hub names the value keys. */
  index: number;
  demandKey: string;
  windowKey: string;
  /** The real name from the pick, else `Zone N`. */
  name: string;
  /** False when `name` is the placeholder — the zone has no pick (or no data). */
  named: boolean;
  /** The source value's name, when it says more than the device's. */
  detail: string | null;
  /** The zone's demand in %, or null when it has no reading at all. */
  demand: number | null;
  /** A window contact in this zone is open: the hub reads its demand as 0. */
  windowOpen: boolean;
  /** Above the threshold, window closed — this zone is why demand rises. */
  asking: boolean;
}

/** The contact's own readings, as a surface shows them. */
export interface HeatDemandState {
  /** The master switch; a device that never wrote it reads as enabled. */
  enabled: boolean;
  /** `heat_demand` — what the handler decided, not what the zones imply now. */
  contact: boolean;
  /** Zones counting on the contact's own basis (see `heatDemandActiveCount`). */
  activeCount: number;
  /** Zones above the threshold right now. */
  askingCount: number;
  /** Every zone is silent — the no-source watchdog is (or will be) armed. */
  noSource: boolean;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** A finite number from a settings bag, in the spellings the hub stores. */
function numberSetting(settings: unknown, key: string): number | undefined {
  const raw = asRecord(settings)[key];
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

/** A numeric reading, in the spellings a value row carries it. */
function numericReading(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function truthy(raw: unknown): boolean {
  return raw === true || raw === 1 || raw === 'on' || raw === 'true';
}

/** The `{ valueId }` a `value_picker` setting carries, or null. */
export function pickedValueId(settings: unknown, settingKey: string): string | null {
  const pick = asRecord(asRecord(settings)[settingKey]);
  return typeof pick.valueId === 'string' && pick.valueId ? pick.valueId : null;
}

/**
 * The handler's thresholds, read the way `HeatDemandControllerHandler` reads
 * them: the class key first, the handler's older key second, then its default.
 */
export function heatDemandThresholds(settings: unknown): HeatDemandThresholds {
  const threshold =
    numberSetting(settings, 'min_demand_threshold') ??
    numberSetting(settings, 'threshold_percent') ??
    20;
  const hysteresis = numberSetting(settings, 'hysteresis_percent') ?? 5;

  return {
    threshold,
    hysteresis,
    releaseAt: Math.max(0, threshold - hysteresis),
    minOnSeconds: Math.max(0, numberSetting(settings, 'min_on_seconds') ?? 180),
    minOffSeconds: Math.max(0, numberSetting(settings, 'min_off_seconds') ?? 300),
    noSourceTimeoutSeconds: Math.max(0, numberSetting(settings, 'no_source_timeout_seconds') ?? 600),
  };
}

/** Whether a source value's name is worth showing beside the device's. */
function isInformativeValueName(name: string): boolean {
  const normalized = name.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  return normalized !== '' && !GENERIC_VALUE_NAMES.has(normalized);
}

/**
 * The zones to draw: everything the settings configure, plus anything the
 * device actually reports. `zone_count` alone would hide a zone whose values
 * arrived before its setting did, and the values alone would hide a zone that
 * is configured but has never reported.
 *
 * @param values   The device's readings by value key (live map preferred).
 * @param sourceOf Resolves a source `valueId` to the device and value that back it.
 */
export function heatDemandZones(
  settings: unknown,
  values: Record<string, unknown>,
  sourceOf: (valueId: string) => HeatDemandSource | null = () => null,
): HeatDemandZone[] {
  const thresholds = heatDemandThresholds(settings);
  const configured = numberSetting(settings, 'zone_count') ?? DEFAULT_ZONE_COUNT;
  const zoneCount = Math.min(ZONE_LIMIT, Math.max(1, Math.round(configured)));

  /** Every zone the settings configure or the device actually reports. */
  const relevant: { index: number; sourceId: string | null; demand: number | null }[] = [];
  for (let index = 1; index <= ZONE_LIMIT; index += 1) {
    const sourceId = pickedValueId(settings, `demand${index}_source`);
    const demand = numericReading(values[`demand${index}`]);
    if (sourceId !== null || demand !== null) relevant.push({ index, sourceId, demand });
  }

  // Nothing configured and nothing reported — a hub whose payload carries no
  // settings (the cloud-compact shape) as much as an empty controller. Listing
  // placeholders would invent zones the device never had.
  if (relevant.length === 0) return [];

  const lastRelevant = relevant[relevant.length - 1].index;
  const listLimit = Math.min(ZONE_LIMIT, Math.max(zoneCount, lastRelevant));

  const zones: HeatDemandZone[] = [];
  /** The value name behind each zone, kept for the duplicate-name pass below. */
  const rawNames: string[] = [];

  for (let index = 1; index <= listLimit; index += 1) {
    const demandKey = `demand${index}`;
    const windowKey = `window_open${index}`;
    const sourceId = pickedValueId(settings, `demand${index}_source`);
    const demand = numericReading(values[demandKey]);

    const source = sourceId ? sourceOf(sourceId) : null;
    const sourceName = source?.deviceName.trim() ?? '';
    const valueName = source?.valueName?.trim() ?? '';
    const windowOpen = truthy(values[windowKey]);

    zones.push({
      index,
      demandKey,
      windowKey,
      name: sourceName || `Zone ${index}`,
      named: sourceName !== '',
      detail: isInformativeValueName(valueName) ? valueName : null,
      demand,
      windowOpen,
      asking: demand !== null && !windowOpen && demand > thresholds.threshold,
    });
    rawNames.push(valueName);
  }

  // Two zones off one device (a boiler's heating and cooling outputs, a
  // thermostat's stages) would read as the same name twice; the value's own
  // name is what tells them apart, so it is shown even when it is generic.
  const nameCounts = new Map<string, number>();
  for (const zone of zones) nameCounts.set(zone.name, (nameCounts.get(zone.name) ?? 0) + 1);
  zones.forEach((zone, position) => {
    if (zone.detail === null && (nameCounts.get(zone.name) ?? 0) > 1) {
      zone.detail = rawNames[position] || null;
    }
  });

  return zones;
}

/**
 * How many zones count toward the contact, on the contact's own basis — the
 * same fold the handler applies: above the threshold while the contact is off,
 * above the release level while it is on. Counting with the threshold alone is
 * what once logged `Heat demand ON {activeCount: 0}`.
 */
export function heatDemandActiveCount(
  zones: HeatDemandZone[],
  contact: boolean,
  thresholds: HeatDemandThresholds,
): number {
  return zones.filter((zone) => {
    if (zone.demand === null || zone.windowOpen) return false;
    return contact ? zone.demand > thresholds.releaseAt : zone.demand > thresholds.threshold;
  }).length;
}

/** The contact, the switch and the counts a surface shows. */
export function heatDemandState(
  zones: HeatDemandZone[],
  values: Record<string, unknown>,
  thresholds: HeatDemandThresholds,
): HeatDemandState {
  const contact = truthy(values.heat_demand);
  return {
    enabled: values.enabled === undefined || values.enabled === null ? true : truthy(values.enabled),
    contact,
    activeCount: heatDemandActiveCount(zones, contact, thresholds),
    askingCount: zones.filter((zone) => zone.asking).length,
    noSource: zones.every((zone) => zone.demand === null),
  };
}
